import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { useLanguage } from "../../context/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { MEDIA_URL } from "../../config/constants";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isMobileLanguageDropdownOpen, setIsMobileLanguageDropdownOpen] = useState(false);
  const { currentLanguage, changeLanguage, getLocalizedPath } = useLanguage();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to determine if a link is active
  const isActiveLink = (path) => {
    if (path === "/" && location.pathname === "/") {
      return true;
    }
    if (path !== "/" && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  const trackNavigation = (page, section = 'header') => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Click',
      label: `${section}_${page}`
    });
  };

  const toggleMobileMenu = () => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Mobile Menu Toggle',
      label: isMobileMenuOpen ? 'close' : 'open'
    });
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleLanguageDropdown = () => {
    ReactGA.event({
      category: 'User Interaction',
      action: 'Language Dropdown',
      label: 'toggle'
    });
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const toggleMobileLanguageDropdown = () => {
    ReactGA.event({
      category: 'User Interaction',
      action: 'Mobile Language Dropdown',
      label: 'toggle'
    });
    setIsMobileLanguageDropdownOpen(!isMobileLanguageDropdownOpen);
  };

  const handleLanguageSelect = (language) => {
    ReactGA.event({
      category: 'User Interaction',
      action: 'Language Change',
      label: language
    });
    changeLanguage(language.toLowerCase());
    setIsLanguageDropdownOpen(false);
  };

  const handleMobileLanguageSelect = (language) => {
    ReactGA.event({
      category: 'User Interaction',
      action: 'Mobile Language Change',
      label: language
    });
    changeLanguage(language.toLowerCase());
    setIsMobileLanguageDropdownOpen(false);
  };

  const handleBookNow = () => {
    ReactGA.event({
      category: 'CTA',
      action: 'Book Now Click',
      label: 'header'
    });
    navigate(getLocalizedPath("/catalog"));
  };

  return (
    <header className="header">
      {/* Mobile Header */}
      <div className="mobile-header">
        <Link to={getLocalizedPath("/")} onClick={() => trackNavigation('logo', 'mobile')}>
          <img
            src={`${MEDIA_URL}/images/sun_catchers_logo.png`}
            className="mobile-logo"
            alt="Sun Catchers Logo"
          />
        </Link>
        <button
          className={`burger-menu ${isMobileMenuOpen ? "active" : ""}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Desktop Navigation */}
      <div className="navigation-links">
        <Link to={getLocalizedPath("/")} className={isActiveLink("/") ? "home-link" : "nav-link"} onClick={() => trackNavigation('home', 'desktop')}>
          {t('header.navigation.home')}
        </Link>
        <Link to={getLocalizedPath("/catalog")} className={isActiveLink("/catalog") ? "home-link" : "nav-link"} onClick={() => trackNavigation('campervans', 'desktop')}>
          {t('header.navigation.campervans')}
        </Link>
        <Link to={getLocalizedPath("/surf")} className={isActiveLink("/surf") ? "home-link" : "nav-link"} onClick={() => trackNavigation('surf', 'desktop')}>
          {t('header.navigation.surf')}
        </Link>
        <Link to={getLocalizedPath("/blog")} className={isActiveLink("/blog") ? "home-link" : "nav-link"} onClick={() => trackNavigation('van_tips', 'desktop')}>
          {t('header.navigation.vanTips')}
        </Link>
        {/* TODO: Uncomment when Sale page is ready
        <Link to={getLocalizedPath("/sale")} className={isActiveLink("/sale") ? "home-link" : "nav-link"} onClick={() => trackNavigation('sale', 'desktop')}>
          {t('header.navigation.sale')}
        </Link>
        */}
      </div>

      <Link to={getLocalizedPath("/")} onClick={() => trackNavigation('logo', 'desktop')}>
        <img
          src={`${MEDIA_URL}/images/sun_catchers_logo.png`}
          className="logo"
          alt="Sun Catchers Logo"
        />
      </Link>

      <div className="right-navigation-links">
        <Link to={getLocalizedPath("/about")} className={isActiveLink("/about") ? "home-link" : "nav-link"} onClick={() => trackNavigation('about', 'desktop')}>
          {t('header.navigation.about')}
        </Link>
        <Link to={getLocalizedPath("/contact")} className={isActiveLink("/contact") ? "home-link" : "nav-link"} onClick={() => trackNavigation('contact', 'desktop')}>
          {t('header.navigation.contact')}
        </Link>
        <Link to={getLocalizedPath("/faq")} className={isActiveLink("/faq") ? "home-link" : "nav-link"} onClick={() => trackNavigation('faq', 'desktop')}>
          {t('header.navigation.faq')}
        </Link>
        <div className="language-selector" onClick={toggleLanguageDropdown}>
          <img
            src={`${MEDIA_URL}/icons/navigation/globe-icon.svg`}
            className="language-icon"
            alt=""
          />
          <div className="nav-link">{currentLanguage.toUpperCase()}</div>
          <img
            src={`${MEDIA_URL}/icons/navigation/chevron-down.svg`}
            className={`dropdown-icon ${isLanguageDropdownOpen ? "rotated" : ""}`}
            alt=""
          />
          {isLanguageDropdownOpen && (
            <div className="language-dropdown">
              <div
                className={`language-option ${currentLanguage === "en" ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageSelect("en");
                }}
              >
                EN - {t('header.language.en')}
              </div>
              <div
                className={`language-option ${currentLanguage === "pt" ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageSelect("pt");
                }}
              >
                PT - {t('header.language.pt')}
              </div>
              <div
                className={`language-option ${currentLanguage === "es" ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageSelect("es");
                }}
              >
                ES - {t('header.language.es')}
              </div>
              <div
                className={`language-option ${currentLanguage === "it" ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageSelect("it");
                }}
              >
                IT - {t('header.language.it')}
              </div>
              <div
                className={`language-option ${currentLanguage === "fr" ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLanguageSelect("fr");
                }}
              >
                FR - {t('header.language.fr')}
              </div>
            </div>
          )}
        </div>
        <div className="book-now-button" onClick={handleBookNow}>
          <div className="button-state-layer">{t('header.bookNow')}</div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? "active" : ""}`}
      >
        <nav className="mobile-navigation">
          <Link
            to={getLocalizedPath("/")}
            className={`mobile-nav-link ${isActiveLink("/") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('home', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.home')}
          </Link>
          <Link
            to={getLocalizedPath("/catalog")}
            className={`mobile-nav-link ${isActiveLink("/catalog") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('campervans', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.campervans')}
          </Link>
          <Link
            to={getLocalizedPath("/surf")}
            className={`mobile-nav-link ${isActiveLink("/surf") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('surf', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.surf')}
          </Link>
          <Link
            to={getLocalizedPath("/blog")}
            className={`mobile-nav-link ${isActiveLink("/blog") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('van_tips', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.vanTips')}
          </Link>
          {/* TODO: Uncomment when Sale page is ready
          <Link
            to={getLocalizedPath("/sale")}
            className={`mobile-nav-link ${isActiveLink("/sale") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('sale', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.sale')}
          </Link>
          */}
          <Link
            to={getLocalizedPath("/about")}
            className={`mobile-nav-link ${isActiveLink("/about") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('about', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.about')}
          </Link>
          <Link
            to={getLocalizedPath("/contact")}
            className={`mobile-nav-link ${isActiveLink("/contact") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('contact', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.contact')}
          </Link>
          <Link
            to={getLocalizedPath("/faq")}
            className={`mobile-nav-link ${isActiveLink("/faq") ? "home-link" : ""}`}
            onClick={() => { trackNavigation('faq', 'mobile'); toggleMobileMenu(); }}
          >
            {t('header.navigation.faq')}
          </Link>
          <div className="mobile-language-selector" onClick={toggleMobileLanguageDropdown}>
            <img
              src={`${MEDIA_URL}/icons/navigation/globe-icon.svg`}
              className="language-icon"
              alt=""
            />
            <span>{currentLanguage.toUpperCase()}</span>
            <img
              src={`${MEDIA_URL}/icons/navigation/chevron-down.svg`}
              className={`dropdown-icon ${isMobileLanguageDropdownOpen ? "rotated" : ""}`}
              alt=""
            />
            {isMobileLanguageDropdownOpen && (
              <div className="mobile-language-dropdown">
                <div
                  className={`language-option ${currentLanguage === "en" ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileLanguageSelect("en");
                  }}
                >
                  EN - {t('header.language.en')}
                </div>
                <div
                  className={`language-option ${currentLanguage === "pt" ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileLanguageSelect("pt");
                  }}
                >
                  PT - {t('header.language.pt')}
                </div>
                <div
                  className={`language-option ${currentLanguage === "es" ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileLanguageSelect("es");
                  }}
                >
                  ES - {t('header.language.es')}
                </div>
                <div
                  className={`language-option ${currentLanguage === "it" ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileLanguageSelect("it");
                  }}
                >
                  IT - {t('header.language.it')}
                </div>
                <div
                  className={`language-option ${currentLanguage === "fr" ? "selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileLanguageSelect("fr");
                  }}
                >
                  FR - {t('header.language.fr')}
                </div>
              </div>
            )}
          </div>
          <div
            className="mobile-book-button"
            onClick={() => {
              ReactGA.event({
                category: 'CTA',
                action: 'Book Now Click',
                label: 'mobile'
              });
              navigate(getLocalizedPath("/catalog"));
              toggleMobileMenu();
            }}
          >
            <div className="button-state-layer">{t('header.bookNow')}</div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
